import { AxiosInstance, AxiosRequestConfig } from 'axios';

import { axios } from '../../../../api/axios-instance';
import { userEndpoints } from '../../../../constants';
import { RolesEnum } from '../../../../enum/roles.enum';
import { PaginationRequest } from '../../../../types/pagination-request';
import { SortRequest } from '../../../../types/sort-request';
import { IProfile } from '../../entities/profile';
import {
  UpdateUserBalanceInput,
  UserResponse,
  UsersListResponse,
  UsersResponse,
} from '../../schemas/user.schema';

class UsersApi {
  #requestInstance: AxiosInstance;

  constructor(axiosInstance: AxiosInstance) {
    this.#requestInstance = axiosInstance;
  }

  getUsers = async (): Promise<UserResponse[]> => this.#requestInstance.get(userEndpoints.user);

  postUsers = async (): Promise<UserResponse[]> => this.#requestInstance.post(userEndpoints.user);

  bootstrapCurrentUser = async (): Promise<UserResponse> =>
    this.#requestInstance.post(`${userEndpoints.user}/bootstrap`);

  getUser = async (id: number): Promise<UserResponse> =>
    this.#requestInstance.get(userEndpoints.getUserById(id));

  getMe = async (): Promise<IProfile> => this.#requestInstance.get(userEndpoints.me);

  logOut = async (config: AxiosRequestConfig): Promise<void> =>
    this.#requestInstance.get(userEndpoints.logout, config);

  createUser = async (userData: IProfile): Promise<UserResponse> =>
    this.#requestInstance.post(userEndpoints.user, userData);

  updateUser = async (input: {
    userData: Pick<IProfile, 'fullName' | 'phone' | 'birthDate' | 'email'> & { sizeId?: number };
  }): Promise<void> => this.#requestInstance.patch(userEndpoints.me, input.userData);

  updateUserByAdmin = async (input: {
    id: number;
    userData: Pick<IProfile, 'phone' | 'birthDate' | 'email' | 'hireDate' | 'balance'> & {
      adminId: number;
      role?: RolesEnum;
      pixelsHistoryDescription?: string;
    };
  }): Promise<void> =>
    this.#requestInstance.patch(userEndpoints.updateUserById(input.id), input.userData);

  updateUserBalance = async (input: {
    id: number;
    balanceData: UpdateUserBalanceInput;
  }): Promise<void> =>
    this.#requestInstance.patch(userEndpoints.getUserBalanceById(input.id), input.balanceData);

  getPaginatedList = async (
    data: PaginationRequest & SortRequest<string>
  ): Promise<UsersResponse> => {
    return await this.#requestInstance.get(userEndpoints.user, { params: data });
  };

  getPaginatedFiredUsers = async (
    data: PaginationRequest & SortRequest<string>
  ): Promise<UsersResponse> => {
    return await this.#requestInstance.get(userEndpoints.firedUsers, { params: data });
  };

  getPaginatedDeletedUsers = async (
    data: PaginationRequest & SortRequest<string>
  ): Promise<UsersResponse> => {
    return await this.#requestInstance.get(userEndpoints.deletedUsers, { params: data });
  };

  getAllUserNames = async (): Promise<UsersListResponse> => {
    return await this.#requestInstance.get(userEndpoints.usersList);
  };

  onFire = async (id: number, isFired: boolean): Promise<void> => {
    return this.#requestInstance.patch(userEndpoints.fireUserById(id), { isFired });
  };

  onDelete = async (id: number, isDeleted: boolean): Promise<void> => {
    return this.#requestInstance.patch(userEndpoints.deleteUserById(id), { isDeleted });
  };

  loginUser = async (): Promise<void> => this.#requestInstance.get(userEndpoints.getUserLogin());
}

export const usersApi = new UsersApi(axios);
